import { createFileRoute, Link } from '@tanstack/react-router'
import { FileTextIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useOutstanding } from '@/api/payments'
import type { OutstandingChannel } from '@/api/payments'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'

// Outstanding per Sales Channel (UI-SCREENS S-09, 06c #186): what every channel still owes, the
// unpaid and partially paid reports behind it, and the total. Owner only — Outstanding is a
// revenue figure Staff never see, and the API answers them with 403.
export const Route = createFileRoute('/w/$slug/sales-reports/outstanding')({
  component: OutstandingScreen,
  staticData: { breadcrumb: 'payments.outstandingScreen.title' },
})

function OutstandingScreen() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const outstanding = useOutstanding(slug)

  return (
    <section className="flex max-w-3xl flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{t('payments.outstandingScreen.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('payments.outstandingScreen.subtitle')}</p>
      </div>

      {outstanding.isPending ? (
        <LoadingSkeleton />
      ) : outstanding.isError || !outstanding.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : (
        <>
          <Card>
            <CardContent className="flex flex-wrap items-baseline justify-between gap-2 py-4">
              <span className="text-muted-foreground">{t('payments.outstandingScreen.total')}</span>
              <span className="font-heading text-lg font-semibold">
                Rp {formatMoney(outstanding.data.outstanding_amount)}
              </span>
            </CardContent>
          </Card>
          {outstanding.data.channels.length === 0 ? (
            <RecordListEmpty>{t('payments.outstandingScreen.empty')}</RecordListEmpty>
          ) : (
            <RecordList>
              {outstanding.data.channels.map((row) => (
                <ChannelRow key={row.sales_channel.id} slug={slug} row={row} />
              ))}
            </RecordList>
          )}
        </>
      )}
    </section>
  )
}

// One channel: what it owes as the figure, and its unpaid reports nested under it. The report
// links sit above the row's stretched channel link (`relative z-10`), so each opens its report.
function ChannelRow({ slug, row }: { slug: string; row: OutstandingChannel }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      title={
        <RecordLink to="/w/$slug/channels/$channelId" params={{ slug, channelId: String(row.sales_channel.id) }}>
          {row.sales_channel.name}
        </RecordLink>
      }
      badges={
        row.sales_channel.archived ? <Badge variant="outline">{t('payments.outstandingScreen.archived')}</Badge> : null
      }
      meta={[
        {
          icon: FileTextIcon,
          label: t('salesReports.title'),
          value: t('payments.outstandingScreen.list.reports', { count: row.reports.length }),
        },
      ]}
      figure={`Rp ${formatMoney(row.outstanding_amount)}`}
    >
      <ul className="flex flex-col divide-y">
        {row.reports.map((report) => (
          <li key={report.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 last:pb-0">
            <Link
              to="/w/$slug/sales-reports/$reportId"
              params={{ slug, reportId: String(report.id) }}
              className="relative z-10 underline-offset-4 hover:underline"
            >
              {report.sales_report_no}
            </Link>
            <span className="text-muted-foreground">
              {t('salesReports.period', {
                from: formatBusinessDate(report.period_start),
                to: formatBusinessDate(report.period_end),
              })}
            </span>
            <Badge variant="outline">{t(`salesReports.statuses.${report.status}`)}</Badge>
            <span className="ml-auto tabular-nums">Rp {formatMoney(report.outstanding_amount)}</span>
          </li>
        ))}
      </ul>
    </RecordListItem>
  )
}
